// src/pages/CasinoPage.jsx
import React, { useState } from 'react';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';

const CasinoPage = () => {
  const [activeCategory, setActiveCategory] = useState('featured');
  const [demoBalance, setDemoBalance] = useState(10000); // Demo money

  const casinoGames = {
    featured: [
      { id: 1, name: 'Lightning Roulette', provider: 'Evolution', type: 'roulette', demo: true, minBet: 1, maxBet: 1000 },
      { id: 2, name: 'Mega Moolah', provider: 'Microgaming', type: 'slots', demo: true, jackpot: '€4.5M' },
      { id: 3, name: 'Blackjack Live', provider: 'Playtech', type: 'card', demo: true, minBet: 5, dealers: 8 },
    ],
    slots: [
      { id: 4, name: 'Starburst', provider: 'NetEnt', volatility: 'Low', rtp: '96.1%' },
      { id: 5, name: 'Bonanza', provider: 'Big Time Gaming', volatility: 'High', rtp: '96.0%' },
      { id: 6, name: 'Book of Dead', provider: 'Play\'n GO', volatility: 'High', rtp: '96.2%' },
    ],
    live: [
      { id: 7, name: 'Dream Catcher', provider: 'Evolution', type: 'wheel', host: 'Lucy' },
      { id: 8, name: 'Monopoly Live', provider: 'Evolution', type: 'wheel', host: 'Andi' },
      { id: 9, name: 'Lightning Dice', provider: 'Evolution', type: 'dice', host: 'Michael' },
    ]
  };

  const playDemo = (gameId) => {
    alert(`Playing demo of game ID: ${gameId}\nDemo Balance: $${demoBalance}`);
    // In a real app, this would launch the game in demo mode
  };

  return (
    <div className="min-h-screen bg-deep-black">
      <Header />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Casino Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-white mb-2">🎰 Casino Games</h1>
          <p className="text-gray-400">Play the best casino games with demo money</p>
          
          {/* Demo Balance */}
          <div className="mt-4 bg-gradient-to-r from-purple-900/30 to-blue-900/30 rounded-xl p-4">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-lg font-bold text-white">Demo Balance</h3>
                <p className="text-gray-300 text-sm">Practice with virtual money</p>
              </div>
              <div className="text-right">
                <div className="text-3xl font-bold text-green-400">${demoBalance.toLocaleString()}</div>
                <button 
                  onClick={() => setDemoBalance(10000)}
                  className="text-sm text-blue-400 hover:text-blue-300"
                >
                  Reset to $10,000
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Game Categories */}
        <div className="flex space-x-2 mb-6 overflow-x-auto pb-2">
  {['featured', 'slots', 'live', 'table', 'jackpots', 'new'].map(category => (
    <button
      key={category}
      onClick={() => setActiveCategory(category)}
      className={`px-4 py-2 rounded-lg whitespace-nowrap min-w-fit flex-shrink-0 ${
        activeCategory === category
          ? 'bg-purple-600 text-white'
          : 'bg-gray-800 text-gray-300 hover:bg-gray-700'
      }`}
    >
      {category.charAt(0).toUpperCase() + category.slice(1)}
    </button>
  ))}
</div>

        {/* Games Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
          {casinoGames[activeCategory]?.map(game => (
            <div key={game.id} className="bg-gray-900 rounded-xl overflow-hidden hover:transform hover:scale-[1.02] transition-transform">
              {/* Game Image Placeholder */}
              <div className="relative h-48 bg-gradient-to-br from-purple-900/50 to-blue-900/50">
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="text-6xl">
                    {game.type === 'roulette' && '🎡'}
                    {game.type === 'slots' && '🎰'}
                    {game.type === 'card' && '🎴'}
                    {game.type === 'wheel' && '🎡'}
                    {game.type === 'dice' && '🎲'}
                    {!game.type && '🃏'}
                  </div>
                </div>
                {game.demo && (
                  <div className="absolute top-4 left-4 bg-green-600 text-white px-3 py-1 rounded-full text-sm">
                    DEMO
                  </div>
                )}
                {game.jackpot && (
                  <div className="absolute top-4 right-4 bg-yellow-600 text-white px-3 py-1 rounded-full text-sm">
                    JACKPOT
                  </div>
                )}
              </div>
              
              {/* Game Info */}
              <div className="p-4">
                <div className="flex justify-between items-start mb-3">
                  <div>
                    <h3 className="text-lg font-bold text-white">{game.name}</h3>
                    <p className="text-gray-400 text-sm">{game.provider}</p>
                  </div>
                  {game.rtp && (
                    <div className="bg-blue-900/30 text-blue-300 px-2 py-1 rounded text-sm">
                      {game.rtp} RTP
                    </div>
                  )}
                </div>
                
                {/* Game Details */}
                <div className="space-y-2 mb-4">
                  {game.minBet && (
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-400">Min Bet:</span>
                      <span className="text-white">${game.minBet}</span>
                    </div>
                  )}
                  {game.maxBet && (
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-400">Max Bet:</span>
                      <span className="text-white">${game.maxBet}</span>
                    </div>
                  )}
                  {game.volatility && (
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-400">Volatility:</span>
                      <span className={`font-semibold ${
                        game.volatility === 'Low' ? 'text-green-400' : 
                        game.volatility === 'Medium' ? 'text-yellow-400' : 
                        'text-red-400'
                      }`}>
                        {game.volatility}
                      </span>
                    </div>
                  )}
                  {game.host && (
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-400">Host:</span>
                      <span className="text-white">{game.host}</span>
                    </div>
                  )}
                </div>
                
                {/* Play Button */}
                <button
                  onClick={() => playDemo(game.id)}
                  className="w-full bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-500 hover:to-blue-500 text-white py-3 rounded-lg font-semibold transition-all"
                >
                  {game.demo ? 'Play Demo' : 'Play for Real'}
                </button>
              </div>
            </div>
          ))}
        </div>

        {/* Casino Information */}
        <div className="bg-gray-900 rounded-xl p-6">
          <h3 className="text-xl font-bold text-white mb-4">🎯 Responsible Gaming</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="text-center">
              <div className="text-4xl mb-2">💰</div>
              <h4 className="font-bold text-white mb-2">Set Limits</h4>
              <p className="text-gray-300 text-sm">Set deposit, loss, and time limits to control your gaming.</p>
            </div>
            <div className="text-center">
              <div className="text-4xl mb-2">🔒</div>
              <h4 className="font-bold text-white mb-2">Secure & Fair</h4>
              <p className="text-gray-300 text-sm">All games are tested and certified for fairness.</p>
            </div>
            <div className="text-center">
              <div className="text-4xl mb-2">📱</div>
              <h4 className="font-bold text-white mb-2">24/7 Support</h4>
              <p className="text-gray-300 text-sm">Contact support anytime for help with responsible gaming.</p>
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default CasinoPage;